import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  KeyRound, Download, Copy, RefreshCw, Cpu, ArrowRight,
  Terminal, ShieldCheck, Wifi, WifiOff, PackageOpen,
} from "lucide-react";

export default function Connector() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [connectors, setConnectors] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingExe, setDownloadingExe] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [installerInfo, setInstallerInfo] = useState(null);

  const load = async () => {
    const { data } = await api.get("/connector/status");
    setConnectors(data.connectors || []);
  };
  const loadInstallerInfo = async () => {
    // Reads live installer metadata from the SERVER (size, version, sha) so
    // the "Single N MB installer" copy on this page always matches what
    // retailers actually download — never goes stale after a rebuild.
    try {
      const { data } = await api.get("/connector/installer-info");
      if (data?.available) setInstallerInfo(data);
    } catch { /* non-blocking */ }
  };
  useEffect(() => { load(); loadInstallerInfo(); }, []);

  const generate = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/connector/register");
      setNewKey(data);
      toast.success("Connector key generated. Save it now — it will not be shown again.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRotating(false);
    }
  };

  const copyKey = () => {
    if (!newKey) return;
    navigator.clipboard.writeText(newKey.connector_key).then(() => toast.success("Copied"));
  };

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/connector/download-package", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "MaaPrintConnector.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download connector package");
    } finally {
      setDownloading(false);
    }
  };

  const downloadExe = async () => {
    setDownloadingExe(true);
    try {
      const res = await api.get("/connector/download-installer", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "MaaPrintConnectorSetup.exe"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Installer downloaded. Run it on your Windows PC.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to download installer");
    } finally {
      setDownloadingExe(false);
    }
  };

  const STEPS = [
    { icon: Download, text: "Click the green button to download MaaPrintConnectorSetup.exe (Windows 10/11 64-bit)." },
    { icon: Cpu, text: "Double-click the file. Installer runs immediately — no admin prompt, no wizard." },
    { icon: ArrowRight, text: "The connector launches automatically and signs in with your key. That's it." },
  ];

  const CODE_SNIPPETS = [
    { label: "One-click install (Windows)", code: 'Right-click "install.bat" → Run as administrator' },
    { label: "Manual install", code: "pip install -r requirements.txt" },
    { label: "Run silently (no console)", code: 'wscript "run_connector_silent.vbs"' },
    { label: "Uninstall", code: "uninstall.bat  (Run as administrator)" },
  ];

  const copySnippet = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success("Copied"));
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="connector-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{t("connector.title")}</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">{t("connector.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("connector.subtitle")}</p>
      </div>

      {/* One-click install hero */}
      <Card className="p-6 border-brand-green/40 bg-gradient-to-br from-brand-blue/5 to-brand-green/10 relative overflow-hidden"
            data-testid="connector-hero">
        <div className="absolute inset-0 opacity-10 pointer-events-none hero-grid" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-brand-green/15 text-brand-green flex items-center justify-center flex-shrink-0">
            <PackageOpen size={22} />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-brand-green font-semibold">One-click Windows install</p>
            <h3 className="font-display text-xl font-semibold mt-1">
              MaaPrintConnectorSetup.exe — auto-connects on launch
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {installerInfo
                ? `${installerInfo.size_human} installer for Windows 10 / 11 (64-bit). `
                : "Installer for Windows 10 / 11 (64-bit). "}
              Bundles Python + printer drivers (pywin32), connector, desktop shortcut, and autostart. Your pairing key is baked in — no wizard, no typing. Just download → double-click → done.
            </p>
            {installerInfo && (
              <p className="text-[11px] text-muted-foreground mt-2 font-mono" data-testid="connector-installer-badge">
                Build <b>v{installerInfo.version}</b>{" · "}
                {installerInfo.pywin32_bundled ? "pywin32 bundled ✓" : "pywin32 missing ⚠"}
                {" · "}sha256 {installerInfo.sha256}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 md:w-64">
            <Button
              onClick={downloadExe}
              disabled={downloadingExe}
              className="bg-brand-green hover:bg-brand-green-hover text-white h-12 px-5 w-full text-base"
              data-testid="connector-hero-download-exe-btn"
            >
              <Download size={18} className="mr-2" />
              {downloadingExe ? "Preparing…" : "Download & install"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center leading-snug px-1">
              Windows 10 / 11 64-bit only · signs in automatically
            </p>
          </div>
        </div>
      </Card>

      {/* Action bar */}
      <Card className="p-6 border-border bg-card">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <KeyRound size={18} />
              </div>
              <div>
                <p className="font-medium">{t("connector.yourKey")}</p>
                <p className="text-xs text-muted-foreground">{t("connector.keyWarn")}</p>
              </div>
            </div>
            {newKey ? (
              <div className="rounded-lg border border-brand-green/30 bg-brand-green/5 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-brand-green" />
                  <span className="text-xs uppercase tracking-widest text-brand-green">Active</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input value={newKey.connector_key} readOnly className="font-mono text-xs" data-testid="connector-key-value" />
                  <Button variant="outline" size="icon" onClick={copyKey} data-testid="connector-copy-btn">
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Click <b>{t("connector.generate")}</b> to create your unique agent key.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={generate}
                disabled={rotating}
                className="bg-primary hover:bg-brand-blue-hover"
                data-testid="connector-generate-btn"
              >
                <RefreshCw size={14} className={`mr-2 ${rotating ? "animate-spin" : ""}`} />
                {newKey ? t("connector.regenerate") : t("connector.generate")}
              </Button>
              <Button
                variant="outline"
                onClick={downloadZip}
                disabled={downloading}
                data-testid="connector-download-btn"
              >
                <Download size={14} className="mr-2" />
                {t("connector.downloadPackage")}
              </Button>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              {t("connector.setupSteps")}
            </p>
            <ol className="space-y-3">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={i} className="flex items-start gap-3">
                    <div className="h-7 w-7 rounded-md bg-secondary text-foreground flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                      {i + 1}
                    </div>
                    <div className="flex-1 flex items-start gap-2">
                      <Icon size={14} className="text-primary flex-shrink-0 mt-1" />
                      <span className="text-sm">{s.text}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </Card>

      {/* Connected agents */}
      <Card className="p-6 border-border bg-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold">{t("connector.connected")}</h3>
          <Badge variant="outline">{connectors.length}</Badge>
        </div>
        {connectors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="connector-empty">
            {t("connector.noConnectors")}
          </p>
        ) : (
          <div className="divide-y divide-border">
            {connectors.map((c) => {
              const seen = c.last_seen ? new Date(c.last_seen) : null;
              const online = seen && (Date.now() - seen.getTime() < 60 * 1000);
              return (
                <div key={c.id} className="py-3 flex items-center gap-4" data-testid={`connector-row-${c.id}`}>
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${online ? "bg-brand-green/10 text-brand-green" : "bg-muted text-muted-foreground"}`}>
                    <Cpu size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{c.host || "Unknown host"}</p>
                    <p className="text-xs text-muted-foreground">{c.os || "—"} · v{c.version || "?"}</p>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("connector.lastSeen")}: {seen ? seen.toLocaleString() : "never"}
                  </div>
                  <Badge variant="outline" className={online ? "bg-brand-green/10 text-brand-green border-brand-green/20" : "bg-muted"}>
                    {online ? <><Wifi size={12} className="mr-1" />{t("common.online")}</> :
                              <><WifiOff size={12} className="mr-1" />{t("common.offline")}</>}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Code snippets */}
      <Card className="p-6 border-border bg-card" data-testid="connector-snippets">
        <div className="flex items-center gap-2 mb-4">
          <Terminal size={16} className="text-primary" />
          <h3 className="font-display text-lg font-semibold">Terminal commands</h3>
        </div>
        <div className="space-y-3">
          {CODE_SNIPPETS.map((sn, i) => (
            <div key={i} className="flex items-start gap-3 rounded-lg border border-border bg-secondary/40 p-3">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">{sn.label}</p>
                <code className="text-xs font-mono block break-all">{sn.code}</code>
              </div>
              <Button variant="ghost" size="icon" onClick={() => copySnippet(sn.code)} className="h-8 w-8"
                data-testid={`snippet-copy-${i}`}>
                <Copy size={13} />
              </Button>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-4">
          Requires Python 3.9+ and SumatraPDF on Windows. The connector polls MaaPrint every few seconds
          and prints silently — no popups, no dialogs.
        </p>
      </Card>
    </div>
  );
}
